import { Box } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import { forwardRef } from 'react';
import { toSxArray } from 'src/utils/sx';
import {
  SettingsSectionCardProps,
  SettingsSectionCardVariant,
} from './settings-section-card.types';

const cardSurfaceSx: SystemStyleObject<Theme> = {
  backgroundColor: (theme) =>
    theme.palette.mode === 'light'
      ? theme.palette.custom.OV.sys.surfaceContainerLowest
      : theme.palette.custom.OV.sys.surfaceContainer,
  border: '1px solid',
  borderColor: 'custom.OV.sys.outlineVariant',
  borderRadius: '10px',
};

const cardVariantSx: Record<SettingsSectionCardVariant, SystemStyleObject<Theme>> = {
  rows: {
    overflow: 'hidden',
    '& > *:not(:last-child)': {
      borderBottom: '1px solid',
      borderBottomColor: 'custom.OV.sys.outlineVariant',
    },
  },
  // A sticky header row cannot stick inside a clipping box, so the last child
  // takes the card's bottom corners instead, keeping its hover inside the edge.
  table: {
    overflow: 'visible',
    '& > :last-child': {
      borderBottomLeftRadius: 'inherit',
      borderBottomRightRadius: 'inherit',
    },
  },
};

/**
 * Untitled bordered container for setting rows. The card owns the hairline
 * dividers between adjacent rows (none after the last), so rows stay clean and
 * any number of children can be dropped in.
 */
export const SettingsSectionCard = forwardRef<HTMLDivElement, SettingsSectionCardProps>(
  ({ variant = 'rows', sx, children, ...rest }, ref) => (
    <Box ref={ref} sx={[cardSurfaceSx, cardVariantSx[variant], ...toSxArray(sx)]} {...rest}>
      {children}
    </Box>
  ),
);

SettingsSectionCard.displayName = 'SettingsSectionCard';
